import type { Task, ApiResponse } from './types';
import { apiGet, apiPost, apiPut, apiDelete } from '../services/api';

// Current dataset version (used for reproducible benchmark citation)
export const CURRENT_DATASET_VERSION = 'v1.0';

/** Tag tasks lacking a version with the current dataset version. */
export function tagTaskVersion(task: Task): Task {
  return task.dataset_version ? task : { ...task, dataset_version: CURRENT_DATASET_VERSION };
}

export async function getTask(id: string): Promise<Task> {
  try {
    const res = await apiGet<ApiResponse<Task>>(`/tasks/${id}`);
    if (!res.success || !res.data) {
      throw new Error(res.error ?? 'Failed to fetch task');
    }
    return res.data;
  } catch (err) {
    if (err instanceof TypeError && err.message === 'Failed to fetch') {
      throw new Error('Network error: unable to reach the server');
    }
    throw err;
  }
}

export async function listTasks(competencyId?: string): Promise<Task[]> {
  try {
    const path = competencyId
      ? `/tasks?competency_id=${encodeURIComponent(competencyId)}`
      : '/tasks';
    const res = await apiGet<ApiResponse<Task[]>>(path);
    if (!res.success || !res.data) {
      throw new Error(res.error ?? 'Failed to fetch tasks');
    }
    // Ensure every task has a dataset_version (default to current)
    return res.data.map(tagTaskVersion);
  } catch (err) {
    if (err instanceof TypeError && err.message === 'Failed to fetch') {
      throw new Error('Network error: unable to reach the server');
    }
    throw err;
  }
}

export async function createTask(data: Partial<Task>): Promise<Task> {
  try {
    const res = await apiPost<ApiResponse<Task>>('/tasks', data);
    if (!res.success || !res.data) {
      throw new Error(res.error ?? 'Failed to create task');
    }
    return res.data;
  } catch (err) {
    if (err instanceof TypeError && err.message === 'Failed to fetch') {
      throw new Error('Network error: unable to reach the server');
    }
    throw err;
  }
}

export async function updateTask(id: string, data: Partial<Task>): Promise<Task> {
  try {
    const res = await apiPut<ApiResponse<Task>>(`/tasks/${id}`, data);
    if (!res.success || !res.data) {
      throw new Error(res.error ?? 'Failed to update task');
    }
    return res.data;
  } catch (err) {
    if (err instanceof TypeError && err.message === 'Failed to fetch') {
      throw new Error('Network error: unable to reach the server');
    }
    throw err;
  }
}

export async function deleteTask(id: string): Promise<void> {
  try {
    const res = await apiDelete<ApiResponse<null>>(`/tasks/${id}`);
    if (!res.success) {
      throw new Error(res.error ?? 'Failed to delete task');
    }
  } catch (err) {
    if (err instanceof TypeError && err.message === 'Failed to fetch') {
      throw new Error('Network error: unable to reach the server');
    }
    throw err;
  }
}